import {
  pairHasOpenIssue,
  useTaskActivitiesForServiceRequest,
} from '@commenda/api/tasks';
import { Button, Icons } from '@commenda/kakashi';
import { getServiceRequestServiceId } from '@commenda/types';
import { TaskParty, TaskStatus } from '@prisma/client';
import { useRouter } from 'next/router';
import { WORK_REQUEST_CONFIG } from '../../../incorporation';
import { ServiceRequestTaskLabel } from '../../common/labels';
import { ServiceRequestTaskIconConfig } from '../../common/taskIconConfig';
import { ServiceRequestTaskCard } from '../../layout/components/taskCard/ServiceRequestTaskCard';
import { getTaskLabel } from '../../task-labels';
import { ServiceRequestTaskCardConfig, TCommonTaskProp } from '../../types';
import { IndPvtLtdIncorporationTasks } from '../IN_PVT_LTD/ind-pvt-ltd';

export const FillCompanyDetails = ({
  task,
  context,
  isTimelineView,
  displayNumber,
}: TCommonTaskProp) => {
  const { role, serviceRequest } = context;

  const router = useRouter();

  const currentURL = window.location.href;

  const { data: activityFeed } = useTaskActivitiesForServiceRequest(
    serviceRequest?.id
  );

  // Own task is the fixer; issues are reported from the sibling review task.
  const reviewTask = serviceRequest?.taskGroups
    ?.find((srtg) => srtg.id === task.taskGroupId)
    ?.tasks?.find(
      (srt) =>
        srt.legacyServiceRequestTaskTaskId ===
        IndPvtLtdIncorporationTasks.REVIEW_CORPORATION_DETAILS
    );
  const hasOpenReportedIssue = pairHasOpenIssue(
    activityFeed?.activities,
    task,
    reviewTask
  );

  const openWorkRequest = () => {
    if (WORK_REQUEST_CONFIG[task.workRequest.requestType]) {
      router.push({
        pathname: `${WORK_REQUEST_CONFIG[task.workRequest.requestType].urlPrefix}/${task.workRequestId}`,
        query: {
          previousURL: currentURL,
        },
      });
    } else {
      console.error('The work request type is not configured.');
    }
  };

  const getLabel = (role: TaskParty, status: TaskStatus) =>
    getTaskLabel(
      getServiceRequestServiceId(serviceRequest),
      task.legacyServiceRequestTaskTaskId,
      role,
      status,
      task.title
    ) ?? task.title;

  const config: ServiceRequestTaskCardConfig = {
    getLeftIcon: (status) => ServiceRequestTaskIconConfig[status],
    getStateConfig: (state) => {
      // Restored legacy ACTION_REQUIRED (client fixing) / ISSUE_REPORTED (agent waiting) arms.
      if (hasOpenReportedIssue && state.status !== TaskStatus.COMPLETED) {
        const issueMap = {
          [TaskParty.CLIENT]: {
            actions: (
              <Button size="sm" variant="secondary" onClick={openWorkRequest}>
                Fix Reported Issues
                <Icons.ArrowNarrowRight className="w-4" />
              </Button>
            ),
            label: (
              <ServiceRequestTaskLabel
                label="Issues marked in questionnaire"
                timestamp={task.updatedAt}
                status={task.status}
              />
            ),
          },
          [TaskParty.AGENT]: {
            actions: null,
            label: (
              <ServiceRequestTaskLabel
                label="Client fixing the reported issues"
                timestamp={task.updatedAt}
                status={task.status}
              />
            ),
          },
        };
        const issueConfig = issueMap[state.role];
        if (issueConfig) return issueConfig;
      }

      const stateMap = {
        [TaskParty.CLIENT]: {
          [TaskStatus.TO_DO]: {
            actions: (
              <Button size="sm" onClick={openWorkRequest} variant="primary">
                Start <Icons.ArrowNarrowRight className="w-4" />
              </Button>
            ),
            label: (
              <ServiceRequestTaskLabel
                label={getLabel(TaskParty.CLIENT, TaskStatus.TO_DO)}
              />
            ),
          },
          [TaskStatus.IN_PROGRESS]: {
            actions: (
              <Button size="sm" variant="secondary" onClick={openWorkRequest}>
                Continue <Icons.ArrowNarrowRight className="w-4" />
              </Button>
            ),
            label: (
              <ServiceRequestTaskLabel
                label={getLabel(TaskParty.CLIENT, TaskStatus.IN_PROGRESS)}
                timestamp={task.updatedAt}
                status={task.status}
              />
            ),
          },
          [TaskStatus.COMPLETED]: {
            actions: (
              <Button size="sm" variant="secondary" onClick={openWorkRequest}>
                View <Icons.ArrowNarrowRight className="w-4" />
              </Button>
            ),
            label: (
              <ServiceRequestTaskLabel
                label={getLabel(TaskParty.CLIENT, TaskStatus.COMPLETED)}
                timestamp={task.updatedAt}
                status={task.status}
              />
            ),
          },
        },
        [TaskParty.AGENT]: {
          [TaskStatus.TO_DO]: {
            actions: null,
            label: (
              <ServiceRequestTaskLabel
                label={getLabel(TaskParty.AGENT, TaskStatus.TO_DO)}
              />
            ),
          },
          [TaskStatus.IN_PROGRESS]: {
            actions: null,
            label: (
              <ServiceRequestTaskLabel
                label={getLabel(TaskParty.AGENT, TaskStatus.IN_PROGRESS)}
                timestamp={task.updatedAt}
                status={task.status}
              />
            ),
          },
          [TaskStatus.COMPLETED]: {
            actions: null,
            label: (
              <ServiceRequestTaskLabel
                label={getLabel(TaskParty.AGENT, TaskStatus.COMPLETED)}
                timestamp={task.updatedAt}
                status={task.status}
              />
            ),
          },
        },
      };

      return (
        stateMap[state.role]?.[state.status] ?? {
          label: task.title,
          actions: null,
        }
      );
    },
  };

  return (
    <ServiceRequestTaskCard
      task={task}
      state={{
        role,
        status: task.status,
      }}
      context={context}
      config={config}
      isTimelineView={isTimelineView}
      displayNumber={displayNumber}
    />
  );
};
